import React, {type FC, useMemo} from 'react'
import {useOutletContext, useRouteLoaderData} from '@remix-run/react'
import cn from 'clsx'
import type {ImprovedHomepageDataProps, RootContext, RootLoaderResponse} from '~/types'
import {getTranslation} from '~/localization/translation'
import {getDomainConfig} from '~/domains'
import {useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {IconChevronRightThick} from '~/icons/brg-icon-chevron-right-thick'
import {useHorizontalScrollControls} from '../../../../../hooks/use-horizontal-scroll-controls'
import TopRatedDiscountCard from './top-rated-discount-card'

interface TopRatedDiscountsProps {
  topRatedDiscounts: ImprovedHomepageDataProps['topDiscounts']
}
const TopRatedDiscounts: FC<TopRatedDiscountsProps> = ({topRatedDiscounts}) => {
  const {language, featureFlags, trackAnalytics, locale, traffic, hostname, currencyCode, renderTimestamp, domain} =
    useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles: domainStyles} = getDomainConfig(domain, locale)
  const {clickId, setShowCookieConsentGdpr} = useOutletContext<RootContext>()
  const [sectionRef] = useReportWidgetSeen({
    widget_name: 'improved-home-top-rated-discounts-section',
    widget_place: 'middle',
  })
  const ratingFormatter = useMemo(
    () =>
      new Intl.NumberFormat(language, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    [language],
  )
  const {containerRef, canScrollPrev, canScrollNext, scrollPrev, scrollNext} = useHorizontalScrollControls(
    topRatedDiscounts?.length,
    4,
  )
  const trackingContext = useMemo(
    () => ({
      featureFlags,
      trackAnalytics,
      clickId,
      setShowCookieConsentGdpr,
      locale,
      traffic,
      hostname,
      currencyCode,
      renderTimestamp,
    }),
    [
      clickId,
      currencyCode,
      featureFlags,
      hostname,
      locale,
      renderTimestamp,
      setShowCookieConsentGdpr,
      trackAnalytics,
      traffic,
    ],
  )

  const showTopRatedDiscounts = topRatedDiscounts && topRatedDiscounts.length > 0
  const useFourColumnLayout = (topRatedDiscounts?.length ?? 0) >= 4
  if (!showTopRatedDiscounts) return null
  return (
    <section ref={sectionRef} className={cn(domainStyles.UTILS_MAIN_CONTAINER, 'relative mx-auto mt-5 w-full')}>
      <div className="flex flex-col gap-y-2 mobile:gap-y-1">
        <span className="text-base font-semibold uppercase tracking-widest text-[#61C200] mobile:text-xs">
          {translations.ImprovedHomePage.TopRated.title}
        </span>
        <h2 className="text-[32px] font-extrabold text-[#363636] mobile:text-2xl">
          {translations.ImprovedHomePage.TopRated.topRatedDiscounts}
        </h2>
      </div>
      <div className="relative mt-2 w-full">
        <div
          ref={containerRef}
          className="no-scrollbar flex gap-3 overflow-x-hidden px-1 py-3 tablet:!w-screen tablet:gap-3 tablet:!overflow-x-scroll"
        >
          {topRatedDiscounts?.map((topRatedDiscount, index) => {
            return (
              <TopRatedDiscountCard
                key={`${topRatedDiscount.id}-${index}`}
                topRatedDiscount={topRatedDiscount}
                domainStyles={domainStyles}
                discountBadgeLabel={translations.Badge.discountOff}
                formattedRating={ratingFormatter.format(topRatedDiscount.ratingScore ?? 0)}
                buyItOnAmazonLabel={translations.ImprovedHomePage.TopRated.buyItOnAmazon}
                cardIndex={index}
                useFourColumnLayout={useFourColumnLayout}
                trackingContext={trackingContext}
              />
            )
          })}
        </div>
        {canScrollPrev ? (
          <button
            type="button"
            aria-label="Scroll top rated discounts left"
            onClick={scrollPrev}
            className="absolute -left-10 top-1/2 z-10 hidden size-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-[#4F4F4F] drop-shadow-md transition desktop:flex mobile:hidden"
          >
            <IconChevronRightThick fill="white" className="size-3 -rotate-180" />
          </button>
        ) : null}
        {canScrollNext ? (
          <button
            type="button"
            aria-label="Scroll top rated discounts right"
            onClick={scrollNext}
            className="absolute -right-10 top-1/2 z-10 hidden size-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-[#4F4F4F] drop-shadow-md transition desktop:flex mobile:hidden"
          >
            <IconChevronRightThick fill="white" className="size-3" />
          </button>
        ) : null}
      </div>
    </section>
  )
}

export default TopRatedDiscounts
